import { FC, useEffect, useState } from "react";
import Restricted from "@/consumer/restricted";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { useTranslation } from "react-i18next";
import { IAppointment } from "@/types/Entities";
import { api } from "@/helper/api";
import {
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import AppointmentTreeItemFileList from "@/components/appointmentDashboard/appointmentTreeItemFileList";
import AppointmentTreeItemHeaderbar from "@/components/appointmentDashboard/appointmentTreeItemHeaderbar";
import AppointmentTreeItemSubjectInformation from "@/components/appointmentDashboard/appointmentTreeItemSubjectInformation";
import AppointmentTreeItemWidgets from "@/components/appointmentDashboard/appointmentTreeItemWidgets";
import AppointmentTreeItemUploadButton from "@/components/appointmentDashboard/appointmentTreeItemUploadButton";
import AppointmentTreeItemForms from "@/components/appointmentDashboard/appointmentTreeItemForms";
import styles from "@/components/appointmentDashboard/appointmentTreeView.module.css";
import DocumentTable from "@/components/tables/simpleTableViews/documentTable";
import { useParams } from "react-router-dom";
import AppointmentTreeForm from "@/components/appointmentDashboard/appointmentTreeForm";
import SubjectOverviewAppointments from "@/components/subjectOverview/subjectOverviewAppointments";
import AppointmentDashboardTrackedService from "@/components/appointmentDashboard/appointmentDashboardTrackedService";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GenericNotification from "@/components/notification/genericNotification";
import { useActiveKaerUsers } from "@/helper/customHooks/useActiveKaerUsers";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import { setAppointmentFormPanelExpanded } from "@/components/state/appointmentDashboard/appointmentFormPreferencesSlice";
import AppointmentFormLayout from "@/components/appointmentDashboard/appointmentFormLayout";

interface Props {
  appointmentIdProp: string;
}

export interface formValues {
  id?: string;
  created_by?: string;
  modified_by?: string;
  created_on?: string;
  modified_on?: string;
  unit?: string;
  name?: string;
  value?: number;
  appointment?: string;
  findings?: [];
  image_set?: any[];
  note?: any;
}

const accordionSx = {
  "&.MuiAccordion-root.Mui-expanded": {
    margin: "0",
    position: "inherit",
  },
  "& .MuiAccordionSummary-content.Mui-expanded": {
    margin: "0",
  },
  "& .MuiAccordionSummary-root": {
    minHeight: "48px !important",
  },
  "& .MuiAccordionDetails-root": {
    padding: "0px 16px 16px 16px",
  },
};

const AppointmentTreeView: FC<Props> = ({ appointmentIdProp }) => {
  const { t } = useTranslation();
  const { handleError, errorModal } = useErrorModal();
  const dispatch = useDispatch();
  const { actionsExpanded, detailsExpanded } = useSelector((state: RootState) => state.appointmentFormPreferences);

  const [currentAppointment, setCurrentAppointment]: any =
    useState<IAppointment>();

  const [executedBy, setExecutedBy] = useState<any>("");
  const [assistedBy, setAssistedBy] = useState<any>("");
  const [subjectOfAppointment, setSubjectOfAppointment] = useState<any>();
  const [widgets, setWidgets] = useState<any[]>([]);
  const [associatedForms, setAssociatedForms] = useState<any[]>([]);
  const [reloadCount, setReloadCount] = useState(0);
  const [isLoadingAppointment, setIsLoadingAppointment] = useState(true);
  const [successForNotification, setSuccessForNotification] =
    useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [trackedServicesExpanded, setTrackedServicesExpanded] = useState<
    string | false
  >(false);
  const [hasExpandedTrackedServices, setHasExpandedTrackedServices] =
    useState(false);
  const [currentCustomer, setCurrentCustomer] = useState([]);

  const { userOptions } = useActiveKaerUsers({ onError: handleError });

  const { formId, findingId } = useParams() as {
    formId: string;
    findingId: string;
  };

  useEffect(() => {
    setNotificationMessage("");
    setSuccessForNotification(false);
    if (appointmentIdProp && appointmentIdProp !== "") {
      /*eslint-disable */
      loadAppointment(appointmentIdProp, (responseData: any) => {
        /*eslint-enable */
      });
    } else {
      setCurrentAppointment();
    }
  }, [appointmentIdProp, reloadCount]);

  useEffect(() => {
    setTrackedServicesExpanded(false);
    setHasExpandedTrackedServices(false);
  }, [appointmentIdProp]);

  const loadAppointment = (
    appointmentId: any,
    onSuccessHandler: any = undefined
  ) => {
    const additionalRouteParts: any = {};
    additionalRouteParts["dashboard"] = "/";
    setIsLoadingAppointment(true);
    api.genericApiRequest({
      entity: "appointment",
      method: "get",
      entityId: appointmentId,
      additionalRouteParts: additionalRouteParts,
      successHandler: (res: any) => {
        const currentAppointmentObject = res.data;
        setCurrentAppointment(currentAppointmentObject);
        setExecutedBy(currentAppointmentObject.executed_by?.id);
        setAssistedBy(currentAppointmentObject.assisted_by?.id);
        setSubjectOfAppointment(currentAppointmentObject?.subject);
        setWidgets(res.data.widgets);
        setAssociatedForms(res.data.associated_forms);
        setIsLoadingAppointment(false);

        if (onSuccessHandler) {
          onSuccessHandler(currentAppointmentObject);
        }
      },
      failHandler: (error: any) => {
        setIsLoadingAppointment(false);
        handleError(error);
      },
    });
  };

  const handleSetCurrentAppointment = (appointment: any) => {
    setCurrentAppointment(appointment);
  };

  const handleNotification = (message: any, status: boolean) => {
    setNotificationMessage(message);
    setSuccessForNotification(status);
  };

  const handleTrackedServicesExpand =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setTrackedServicesExpanded(isExpanded ? panel : false);
      if (isExpanded) {
        setHasExpandedTrackedServices(true);
      }
    };

  const handleResponsibleUserChange = (
    selectedOption: any,
    fieldName: "executed_by" | "assisted_by"
  ) => {
    const newValue =
      selectedOption === null
        ? null
        : selectedOption &&
          typeof selectedOption === "object" &&
          selectedOption.value
        ? selectedOption.value
        : null;

    if (newValue === null && selectedOption !== null) {
      return;
    }

    const currentValue =
      fieldName === "executed_by"
        ? currentAppointment?.executed_by?.id
        : currentAppointment?.assisted_by?.id;

    if (fieldName === "executed_by") {
      setExecutedBy(newValue);
    } else {
      setAssistedBy(newValue);
    }

    if (newValue !== currentValue) {
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      const result = { [fieldName]: newValue };
      api.genericApiRequest({
        entity: "appointment",
        method: "put",
        // successHandler: () => {},
        failHandler: (error: any) => {
          handleError(error);
        },
        entityId: String(appointmentIdProp),
        submitData: result,
        config: config,
      });
    }
  };

  const handleFormStatusUpdate = (newStatus: string) => {
    setCurrentAppointment((prevAppointment: IAppointment | undefined) => {
      if (!prevAppointment) return undefined;
      return {
        ...prevAppointment,
        status: newStatus,
      };
    });
  };

  // const renderErrorMessage = (error: any) => {
  //   if (error?.finding_set) {
  //     return error.finding_set.map((finding: any, index: number) => (
  //       <ListItem key={`finding-${index}`} className={styles.noPadding}>
  //         <ListItemText
  //           primary={t("appointment_error_message_incomplete_forms")}
  //           secondary={
  //             <>
  //               <br />
  //               {finding.form_names.map((form: string, idx: number) => (
  //                 <span key={`form-${idx}`} className={styles.blockDisplay}>
  //                   • {form}
  //                 </span>
  //               ))}
  //               <br />
  //               {t("fill_required_fields_and_try_again")}
  //             </>
  //           }
  //         />
  //       </ListItem>
  //     ));
  //   }

  //   return error;
  // };

  useEffect(() => {
    if (!currentAppointment || currentAppointment?.subject?.is_company) {
      setCurrentCustomer([]);
      return;
    }

    api.genericApiRequest({
      method: "get",
      entity: "user",
      parametersToRender: {
        additionalUrlParameters: {
          subject_id: currentAppointment.subject?.id,
          include_deleted: true,
        },
      },
      successHandler: (res: any) => setCurrentCustomer(res.data.results),
      failHandler: (error: any) => handleError(error),
    });
  }, [
    currentAppointment?.subject?.id,
    currentAppointment?.subject?.is_company,
  ]);

  const resetStateOnCloseNotification = () => {
    setSuccessForNotification(false);
  };

  const handleReloadTrigger = () => {
    setReloadCount((count) => count + 1);
  };
  if (!currentAppointment) {
    if (isLoadingAppointment) {
      return <p className={styles.loadingState}>{t("loading")}</p>;
    }
    return null;
  }
  const isAppointmentFormView = formId !== undefined || findingId !== undefined;

  return (
    <>
      <div className={classNames({ [styles.formViewRoot]: isAppointmentFormView })}>
        {/* Header  */}
        <div className={isAppointmentFormView ? undefined : styles.headerBox}>
          <AppointmentTreeItemHeaderbar
            appointment={currentAppointment}
            appointmentId={appointmentIdProp}
            handleSetError={handleError}
            handleSetCurrentAppointment={handleSetCurrentAppointment}
            executedByOptions={userOptions}
            executedByPresetValue={executedBy}
            handleExecutedByChange={(selectedOption: any) =>
              handleResponsibleUserChange(selectedOption, "executed_by")
            }
            assistedByOptions={userOptions}
            assistedByPresetValue={assistedBy}
            handleAssistedByChange={(selectedOption: any) =>
              handleResponsibleUserChange(selectedOption, "assisted_by")
            }
            formId={formId}
            findingId={findingId}
            handleTriggerReload={handleReloadTrigger}
            actionsExpanded={actionsExpanded}
            onActionsExpandedChange={(expanded) => dispatch(setAppointmentFormPanelExpanded({ panel: "actionsExpanded", expanded }))}
            renderAppointmentFormLayout={({ toolbar, actions }) => (
              <AppointmentFormLayout toolbar={toolbar} actions={actions}
                expandedPanelCount={Number(actionsExpanded) + Number(detailsExpanded)}
                details={
                  <AppointmentTreeItemSubjectInformation
                    isAppointmentFormView={true}
                    detailsExpanded={detailsExpanded}
                    onDetailsExpandedChange={(expanded) => dispatch(setAppointmentFormPanelExpanded({ panel: "detailsExpanded", expanded }))}
                    appointment={currentAppointment}
                    onUserUpdated={handleReloadTrigger}
                    currentCustomer={currentCustomer}
                  />
                }>
                <AppointmentTreeForm
                  onStatusUpdated={handleFormStatusUpdate}
                  onFormSubmitted={handleReloadTrigger}
                  statusOfAppointment={
                    typeof currentAppointment.status === "object" ? currentAppointment.status.value : currentAppointment.status
                  }
                />
              </AppointmentFormLayout>
            )}
          />
        </div>

        {!isAppointmentFormView ? (
          <div className={styles.gridWrapper}>
            <section className={styles.subjectSection}>
              <AppointmentTreeItemSubjectInformation
                appointment={currentAppointment}
                onUserUpdated={handleReloadTrigger}
                currentCustomer={currentCustomer}
              />
            </section>
            <section className={styles.leftColumn}>
              <AppointmentTreeItemWidgets
                widgets={widgets}
                appointmentId={appointmentIdProp}
                handleSetError={handleError}
                handleReloadTrigger={handleReloadTrigger}
              />
              <AppointmentTreeItemFileList
                appointment={currentAppointment}
                handleReloadTrigger={handleReloadTrigger}
                handleSetNotification={handleNotification}
              />
              <Accordion
                defaultExpanded={true}
                className={styles.accordion}
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <h2 className={styles.accordionTitle}>
                    {t("appointment_multi")}
                  </h2>
                </AccordionSummary>
                <AccordionDetails>
                  <SubjectOverviewAppointments
                    detailedSubject={subjectOfAppointment}
                  />
                </AccordionDetails>
              </Accordion>
              <Restricted permissionRequired="frontend_view_tracked_service">
                <Accordion
                  className={styles.accordion}
                  expanded={trackedServicesExpanded === "trackedServices"}
                  onChange={handleTrackedServicesExpand("trackedServices")}
                  sx={accordionSx}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <h2 className={styles.accordionTitle}>
                      {t("trackedservice")}
                    </h2>
                  </AccordionSummary>
                  <AccordionDetails>
                    {hasExpandedTrackedServices && currentAppointment?.id && (
                      <AppointmentDashboardTrackedService
                        appointmentId={currentAppointment.id}
                      />
                    )}
                  </AccordionDetails>
                </Accordion>
              </Restricted>
              <Paper className={styles.paperContainer}>
                <h2 className={styles.paperTitle}>{t("all_documents")}</h2>
                <DocumentTable
                  customPageSize={5}
                  folder="all"
                  subjectId={subjectOfAppointment?.id}
                  subjectForDispatch={subjectOfAppointment}
                  customDensity="compact"
                  tableMargin={"0"}
                  tableBoxShadow={"none"}
                  buttonLinkToDocuments={true}
                  showAppointmentDocumentUploadButton={true}
                  appointmentIdForUpload={currentAppointment?.id}
                  handleTriggerReload={handleReloadTrigger}
                />
              </Paper>
            </section>
            <section className={styles.rightColumn}>
              {(!subjectOfAppointment?.is_registered ||
                (subjectOfAppointment?.is_analog &&
                  !subjectOfAppointment?.is_registered)) && (
                <AppointmentTreeItemUploadButton
                  margin="0.625"
                  useCase="formOverview"
                  subject={subjectOfAppointment}
                  handleTriggerReload={handleReloadTrigger}
                />
              )}
              <AppointmentTreeItemForms
                formsToShow="visible"
                forms={associatedForms}
                appointmentId={currentAppointment?.id}
              />
              <AppointmentTreeItemForms
                formsToShow="hidden"
                forms={associatedForms}
                appointmentId={currentAppointment?.id}
              />
            </section>
          </div>
        ) : null}
      </div>
      <GenericNotification
        message={successForNotification ? notificationMessage : null}
        variant={"success"}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />

      {errorModal}
    </>
  );
};

export default AppointmentTreeView;
